import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import StreakCelebrationModal from "../components/StreakCelebrationModal";
import {
  Award, ChevronLeft, Lock, Share2, Flame, TrendingUp,
  Calendar, Star, Sparkles
} from "lucide-react";

// Mandala SVG Pattern for badges (same as in StreakCelebrationModal)
const MandalaPattern = ({ color, size = 120, locked = false }) => {
  const colors = {
    bronze: { primary: "#CD7F32", secondary: "#8B4513", glow: "rgba(205, 127, 50, 0.5)" },
    silver: { primary: "#C0C0C0", secondary: "#808080", glow: "rgba(192, 192, 192, 0.5)" },
    gold: { primary: "#FFD700", secondary: "#DAA520", glow: "rgba(255, 215, 0, 0.6)" }
  };
  
  const { primary, secondary, glow } = colors[color] || colors.gold;
  const opacity = locked ? 0.3 : 1;
  
  return (
    <svg width={size} height={size} viewBox="0 0 200 200" style={{ opacity }}>
      <defs>
        <radialGradient id={`mandala-gradient-${color}-${locked ? 'locked' : 'unlocked'}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={primary} stopOpacity="1" />
          <stop offset="70%" stopColor={secondary} stopOpacity="0.8" />
          <stop offset="100%" stopColor={secondary} stopOpacity="0.3" />
        </radialGradient>
        <filter id={`mandala-glow-${color}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      <circle cx="100" cy="100" r="95" fill="none" stroke={locked ? "#444" : glow} strokeWidth="2" opacity="0.5" />
      
      <g filter={locked ? undefined : `url(#mandala-glow-${color})`}>
        {[...Array(12)].map((_, i) => (
          <ellipse
            key={`outer-${i}`}
            cx="100"
            cy="30"
            rx="15"
            ry="25"
            fill="none"
            stroke={locked ? "#444" : primary}
            strokeWidth="1.5"
            transform={`rotate(${i * 30} 100 100)`}
            opacity="0.8"
          />
        ))}
        
        {[...Array(8)].map((_, i) => (
          <ellipse
            key={`middle-${i}`}
            cx="100"
            cy="50"
            rx="12"
            ry="20"
            fill="none"
            stroke={locked ? "#444" : primary}
            strokeWidth="1.5"
            transform={`rotate(${i * 45 + 22.5} 100 100)`}
            opacity="0.9"
          />
        ))}
        
        {[...Array(6)].map((_, i) => (
          <ellipse
            key={`inner-${i}`}
            cx="100"
            cy="70"
            rx="10"
            ry="15"
            fill={locked ? "#333" : `url(#mandala-gradient-${color}-${locked ? 'locked' : 'unlocked'})`}
            stroke={locked ? "#444" : primary}
            strokeWidth="1"
            transform={`rotate(${i * 60} 100 100)`}
            opacity="0.7"
          />
        ))}
        
        <circle cx="100" cy="100" r="25" fill={locked ? "#333" : `url(#mandala-gradient-${color}-${locked ? 'locked' : 'unlocked'})`} stroke={locked ? "#444" : primary} strokeWidth="2" />
        <circle cx="100" cy="100" r="15" fill="none" stroke={locked ? "#555" : secondary} strokeWidth="1.5" opacity="0.8" />
        <circle cx="100" cy="100" r="8" fill={locked ? "#444" : primary} />
        
        {[...Array(12)].map((_, i) => (
          <circle
            key={`dot-${i}`}
            cx={100 + 40 * Math.cos((i * 30 * Math.PI) / 180)}
            cy={100 + 40 * Math.sin((i * 30 * Math.PI) / 180)}
            r="3"
            fill={locked ? "#444" : primary}
          />
        ))}
      </g>
    </svg>
  );
};

const badgeInfo = {
  7: {
    title: "Sadhak",
    subtitle: "The Seeker",
    sanskrit: "साधक",
    color: "bronze",
    description: "Complete 7 consecutive days of practice",
    message: "Your commitment to inner transformation has awakened the seeker within."
  },
  14: {
    title: "Abhyasi",
    subtitle: "The Practitioner",
    sanskrit: "अभ्यासी",
    color: "silver",
    description: "Complete 14 consecutive days of practice",
    message: "Your discipline is forging a deeper connection with your true self."
  },
  21: {
    title: "Siddh",
    subtitle: "The Accomplished",
    sanskrit: "सिद्ध",
    color: "gold",
    description: "Complete 21 consecutive days of practice",
    message: "You have established a sacred rhythm. This is the mark of true spiritual dedication."
  }
};

export default function BadgeGallery() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [badges, setBadges] = useState([]);
  const [stats, setStats] = useState({ current_streak: 0, longest_streak: 0 });
  const [loading, setLoading] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);

  useEffect(() => {
    fetchBadges();
  }, []);

  const fetchBadges = async () => {
    try {
      const response = await axios.get(`${API}/maya-manthan/streak/badges`);
      setBadges(response.data.badges || []);
      setStats({
        current_streak: response.data.current_streak || 0,
        longest_streak: response.data.longest_streak || 0
      });
    } catch (error) {
      console.error("Error fetching badges:", error);
      toast.error("Failed to load badges");
    } finally {
      setLoading(false);
    }
  };

  const handleShareBadge = (badge) => {
    const celebrationData = {
      milestone: badge.milestone,
      title: badge.title,
      subtitle: badge.subtitle,
      sanskrit: badge.sanskrit,
      badge_color: badge.badge_color,
      message: badgeInfo[badge.milestone]?.message || "",
      encouragement: "Share your achievement with the world!",
      user_name: user?.name?.split(' ')[0] || "Seeker",
      share_text: `🕉️ I earned the '${badge.title}' badge on Maya Manthan after ${badge.milestone} days of spiritual practice! Join me on this sacred journey. #MayaManthan #SpiritualJourney`
    };
    setSelectedBadge(celebrationData);
    setShowCelebration(true);
  };

  const getNextMilestone = () => {
    const milestones = [7, 14, 21];
    for (const m of milestones) {
      if (stats.current_streak < m) {
        return { days: m, remaining: m - stats.current_streak };
      }
    }
    return null;
  };

  const nextMilestone = getNextMilestone();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a1a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a1a] relative" data-testid="badge-gallery-page">
      {/* Background */}
      <div className="fixed inset-0 bg-gradient-to-b from-[#0d0d2b] via-[#0a0a1a] to-[#050510]" />
      
      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            onClick={() => navigate('/maya-manthan')}
            variant="ghost"
            className="text-white/70 hover:text-white hover:bg-white/10"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-serif text-3xl text-white">Your Badges</h1>
            <p className="text-gray-400 text-sm">Milestones on your spiritual journey</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-amber-900/30 to-amber-800/20 border border-amber-500/20 rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <Flame className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 text-xs uppercase tracking-wide">Current Streak</span>
            </div>
            <p className="text-3xl font-bold text-white">{stats.current_streak}</p>
            <p className="text-gray-400 text-xs">days</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 border border-purple-500/20 rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-purple-400" />
              <span className="text-purple-400 text-xs uppercase tracking-wide">Longest Streak</span>
            </div>
            <p className="text-3xl font-bold text-white">{stats.longest_streak}</p>
            <p className="text-gray-400 text-xs">days</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-emerald-900/30 to-emerald-800/20 border border-emerald-500/20 rounded-xl p-4 col-span-2 md:col-span-1"
          >
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 text-xs uppercase tracking-wide">Badges Earned</span>
            </div>
            <p className="text-3xl font-bold text-white">
              {badges.filter(b => b.earned).length} / {badges.length}
            </p>
            <p className="text-gray-400 text-xs">collected</p>
          </motion.div>
        </div>

        {/* Next Milestone Progress */}
        {nextMilestone && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-[#0d0d2b]/60 backdrop-blur-xl border border-white/10 rounded-xl p-6 mb-8"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-white font-medium">Next Milestone</h3>
                <p className="text-gray-400 text-sm">
                  {nextMilestone.remaining} days until {badgeInfo[nextMilestone.days]?.title}
                </p>
              </div>
              <div className="text-right">
                <span className="text-amber-400 text-2xl font-bold">{nextMilestone.days}</span>
                <span className="text-gray-400 text-sm ml-1">days</span>
              </div>
            </div>
            <div className="h-3 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(stats.current_streak / nextMilestone.days) * 100}%` }}
                transition={{ duration: 1, delay: 0.5 }}
                className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full"
              />
            </div>
          </motion.div>
        )}

        {/* Badge Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[7, 14, 21].map((milestone, idx) => {
            const badge = badges.find(b => b.milestone === milestone) || {
              milestone,
              ...badgeInfo[milestone],
              badge_color: badgeInfo[milestone].color,
              earned: false
            };
            const info = badgeInfo[milestone];
            
            return (
              <motion.div
                key={milestone}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + idx * 0.1 }}
                className={`relative bg-gradient-to-br ${
                  badge.earned 
                    ? 'from-[#1a1a3a]/80 to-[#0d0d2b]/60 border-amber-500/30' 
                    : 'from-[#0d0d2b]/60 to-[#0a0a1a]/40 border-white/5'
                } backdrop-blur-xl border rounded-2xl p-6 text-center`}
                data-testid={`badge-card-${milestone}`}
              >
                {/* Locked overlay */}
                {!badge.earned && (
                  <div className="absolute top-4 right-4">
                    <Lock className="w-5 h-5 text-gray-500" />
                  </div>
                )}
                
                {/* Badge visual */}
                <div className="flex justify-center mb-4">
                  <div className="relative">
                    <MandalaPattern 
                      color={info.color} 
                      size={120} 
                      locked={!badge.earned}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className={`text-2xl font-bold ${badge.earned ? 'text-white' : 'text-gray-600'}`}>
                        {milestone}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Badge info */}
                <h3 className={`font-serif text-xl ${badge.earned ? 'text-white' : 'text-gray-500'} mb-1`}>
                  {info.title}
                </h3>
                <p className={`text-sm ${badge.earned ? 'text-gray-300' : 'text-gray-600'} mb-1`}>
                  {info.subtitle}
                </p>
                <p className={`text-lg font-serif ${badge.earned ? 'text-amber-400' : 'text-gray-600'} mb-3`}>
                  {info.sanskrit}
                </p>
                <p className={`text-xs ${badge.earned ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                  {info.description}
                </p>
                
                {/* Action button */}
                {badge.earned ? (
                  <Button
                    onClick={() => handleShareBadge(badge)}
                    className="w-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30"
                    data-testid={`share-badge-${milestone}`}
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share Badge
                  </Button>
                ) : (
                  <div className="text-center py-2">
                    <span className="text-gray-500 text-sm">
                      {stats.current_streak > 0 
                        ? `${milestone - stats.current_streak} days to go`
                        : 'Start your journey'}
                    </span>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Motivation Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-8 text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-4">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-white/70 text-sm">Keep practicing to earn all badges</span>
          </div>
          
          <div className="flex justify-center gap-4">
            <Button
              onClick={() => navigate('/maya-manthan')}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              Continue Practice
            </Button>
            <Button
              onClick={() => navigate('/maya-manthan/leaderboard')}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              View Leaderboard
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Celebration Modal for sharing */}
      <StreakCelebrationModal
        isOpen={showCelebration}
        onClose={() => setShowCelebration(false)}
        celebration={selectedBadge}
        onCelebrated={() => {}}
      />
    </div>
  );
}
